import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import classNames, { Argument } from "classnames/dedupe";
import { Carousel } from "../../ActionsSelectors/Carousel";
import { Typography } from "../../Fundamentals/Typography";
import { Image } from "../../Utility/MediaBuilder/Image";
import { TProductCharacteristic } from "../Product.interface";

export interface IProductColors {
  /** Main data */
  data: TProductCharacteristic[];
  /** Title for the characteristic
   * @default 'Colours' */
  title?: string;
  /** Pre select active color */
  activeColorId?: TProductCharacteristic["id"];
  /** Can unavailable item be selected */
  canSelectUnavailable?: boolean;
  /** Callback for clicking on an item */
  classes?: {
    /** Styles applied to title element */
    title?: Argument;
    carouselWrapper?: Argument;
    carousel?: Argument;
  };
  onSelect?(params: {
    /** Id for quick access */
    id: TProductCharacteristic["id"];
    /** Data itself */
    item: TProductCharacteristic;
    /** In case data will be non-keyed */
    index: number;
  }): void;
}

const ProductColors: FC<IProductColors> = ({
  data,
  activeColorId,
  title,
  canSelectUnavailable = true,
  onSelect,
  classes,
}) => {
  const [activeItemId, setActiveItemId] = useState(
    activeColorId || data[0]?.id,
  );
  const activeItem = useMemo(
    () => data.find((item) => item.id === activeItemId),
    [data, activeItemId],
  );

  /** Reset active for new items */
  useEffect(() => {
    setActiveItemId(activeColorId || data[0]?.id);
  }, [activeColorId, data]);

  const itemSelectionHandler = useCallback(
    (item: TProductCharacteristic, index) => {
      if (!canSelectUnavailable && item.status === "unavailable") return;
      setActiveItemId(item.id);
      onSelect && onSelect({ id: item.id, item, index });
    },
    [canSelectUnavailable, onSelect],
  );

  const carouselWrapperClasses = classNames(classes?.carouselWrapper ?? ``);

  const titleClasses = classNames("product-colour", classes?.title);

  return (
    <>
      <div className={titleClasses}>
        <span>
          <Typography variant="text-capitalize">
            {title || activeItem?.name || "Colour"}
          </Typography>
          :
        </span>
        &nbsp;
        <Typography variant="text-uppercase">{activeItem?.value}</Typography>
      </div>
      <div
        className={carouselWrapperClasses}
        data-testid="ProductColors__carousel"
      >
        <Carousel
          variant="product-colors-carousel"
          classes={{ root: classes?.carousel }}
        >
          {data.map((item, index) => (
            <div
              key={item.id}
              className={classNames("product-preview__item cursor-pointer", {
                unavailable: item.status === "unavailable",
                active: activeItemId === item.id,
              })}
              data-testid="ProductColors__carousel-item"
              onClick={() => itemSelectionHandler(item, index)}
            >
              {item.customImage ? (
                item.customImage
              ) : (
                <Image {...(item?.image?.thumbnail || {})} />
              )}
            </div>
          ))}
        </Carousel>
      </div>
    </>
  );
};

export default ProductColors;
